import React, { useState, useContext, useEffect } from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from 'react-router-dom'
import {
  Form,
  Button,
  ButtonGroup,
  Container,
  Row,
  Col,
  FormGroup,
  FormControl,
  FormLabel,
  Image,
} from 'react-bootstrap'

import config from '../config/config'
import NavigationBar from '../components/NavigationBar'
import AdminNavBar from '../components/Admin/AdminNavBar'
import Footer from '../components/Footer/Footer'
import { AuthContext } from '../contexts/AuthContext'
import { useAuth } from '../contexts/AuthContext'
import { MANAGE_EXTERNAL_LINKS_PATH, LOGIN_PATH } from '../config/routes'
import { useWebsite, setFlashMessage } from '../contexts/WebsiteContext'
import CityMultiSelect from '../components/CityMultiSelect'
import PoiTypeMultiSelect from '../components/PoiTypeMultiSelect'

const ManageExternalLinkEdit = () => {
  const { token } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { logout } = useContext(AuthContext)
  const [formData, setFormData] = useState({
    urlFr: '',
    urlEn: '',
    urlEs: '',
    labelFr: '',
    labelEn: '',
    labelEs: '',
    isAffiliateLink: false,
    linkCriteriaPoiTypes: [],
    linkCriteriaCitys: [],
  })
  const [citys, setCitys] = useState([])
  const [poiTypes, setPoiTypes] = useState([])

  useEffect(() => {
    const fetchCitys = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}city`)
        setCitys(response.data)
      } catch (error) {
        setFlashMessage(t('ERROR_MESSAGE'), 'error')
        // console.error('An error occurred:', error);
      }
    }

    const fetchPoiTypes = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}poi-type`)
        setPoiTypes(response.data)
      } catch (error) {
        setFlashMessage(t('ERROR_MESSAGE'), 'error')
        // console.error('An error occurred:', error);
      }
    }

    fetchCitys()
    fetchPoiTypes()
  }, [t])

  const handleSubmit = async (event) => {
    event.preventDefault()
    try {
      const response = await axios.post(
        `${config.apiUrl}external-link`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      )
      setFlashMessage(t('SUCCESS_CREATED_MESSAGE'), 'success')

      if (response.status === 201) {
        navigate(MANAGE_EXTERNAL_LINKS_PATH)
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        logout()
        navigate(LOGIN_PATH)
      } else if (error.response) {
        setFlashMessage(t('ERROR_MESSAGE'), 'error')
      }
    }
  }

  const handleIsAffiliateLinkChange = (value) => {
    setFormData({ ...formData, isAffiliateLink: value })
  }

  const handlePoiTypesChange = (name) => {
    setFormData((prevFormData) => {
      const updatedPoiTypes = prevFormData.linkCriteriaPoiTypes.includes(name)
        ? prevFormData.linkCriteriaPoiTypes.filter((item) => item !== name)
        : [...prevFormData.linkCriteriaPoiTypes, name]
      return { ...prevFormData, linkCriteriaPoiTypes: updatedPoiTypes }
    })
  }

  const handleCitysChange = (name) => {
    setFormData((prevFormData) => {
      const updatedCitys = prevFormData.linkCriteriaCitys.includes(name)
        ? prevFormData.linkCriteriaCitys.filter((item) => item !== name)
        : [...prevFormData.linkCriteriaCitys, name]
      return { ...prevFormData, linkCriteriaCitys: updatedCitys }
    })
  }

  return (
    <div>
      <NavigationBar />
      <AdminNavBar />
      <Container>
        <h2>{t('MANAGE_EXTERNAL_LINK_NEW_TITLE')}</h2>
        <Form onSubmit={handleSubmit}>
          <Row className="margin-top-20">
            <Col>
              <FormGroup>
                <FormLabel htmlFor="labelFr">
                  {t('LABEL')}{' '}
                  <Image
                    src="/images/icons/flag_fr_icon_24.png"
                    className="flag-label"
                    alt="Label français"
                  />
                </FormLabel>
                <FormControl
                  type="text"
                  id="labelFr"
                  value={formData.labelFr}
                  onChange={(e) =>
                    setFormData({ ...formData, labelFr: e.target.value })
                  }
                  required
                />
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <FormLabel htmlFor="labelEn">
                  {t('LABEL')}{' '}
                  <Image
                    src="/images/icons/flag_en_icon_24.png"
                    className="flag-label"
                    alt="Label anglais"
                  />
                </FormLabel>
                <FormControl
                  type="text"
                  id="labelEn"
                  value={formData.labelEn}
                  onChange={(e) =>
                    setFormData({ ...formData, labelEn: e.target.value })
                  }
                  required
                />
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <FormLabel htmlFor="labelEs">
                  {t('LABEL')}{' '}
                  <Image
                    src="/images/icons/flag_es_icon_24.png"
                    className="flag-label"
                    alt="Label espagnol"
                  />
                </FormLabel>
                <FormControl
                  type="text"
                  id="labelEs"
                  value={formData.labelEs}
                  onChange={(e) =>
                    setFormData({ ...formData, labelEs: e.target.value })
                  }
                  required
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="margin-top-20">
            <Col>
              <FormGroup>
                <FormLabel htmlFor="urlFr">
                  {t('URL')}{' '}
                  <Image
                    src="/images/icons/flag_fr_icon_24.png"
                    className="flag-label"
                    alt="URL français"
                  />
                </FormLabel>
                <FormControl
                  type="text"
                  id="urlFr"
                  value={formData.urlFr}
                  onChange={(e) =>
                    setFormData({ ...formData, urlFr: e.target.value })
                  }
                  required
                />
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <FormLabel htmlFor="urlEn">
                  {t('URL')}{' '}
                  <Image
                    src="/images/icons/flag_en_icon_24.png"
                    className="flag-label"
                    alt="URL anglais"
                  />
                </FormLabel>
                <FormControl
                  type="text"
                  id="urlEn"
                  value={formData.urlEn}
                  onChange={(e) =>
                    setFormData({ ...formData, urlEn: e.target.value })
                  }
                  required
                />
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <FormLabel htmlFor="urlEs">
                  {t('URL')}{' '}
                  <Image
                    src="/images/icons/flag_es_icon_24.png"
                    className="flag-label"
                    alt="URL espagnol"
                  />
                </FormLabel>
                <FormControl
                  type="text"
                  id="urlEs"
                  value={formData.urlEs}
                  onChange={(e) =>
                    setFormData({ ...formData, urlEs: e.target.value })
                  }
                  required
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="margin-top-20">
            <Col>
              <FormGroup>
                <FormLabel style={{ marginRight: '20px' }}>
                  {t('AFFILIATE_LINK')} ?
                </FormLabel>
                <ButtonGroup className="mb-2">
                  <Button
                    variant="primary"
                    onClick={() => handleIsAffiliateLinkChange(true)}
                    className={
                      formData.isAffiliateLink ? 'activated-noclic' : ''
                    }
                  >
                    {t('YES')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleIsAffiliateLinkChange(false)}
                    className={
                      !formData.isAffiliateLink ? 'activated-noclic' : ''
                    }
                  >
                    {t('NO')}
                  </Button>
                </ButtonGroup>
              </FormGroup>
            </Col>
            <Col></Col>
          </Row>

          <Row className="margin-top-20">
            <Col>
              <PoiTypeMultiSelect
                poiTypes={poiTypes}
                selectedPoiTypes={formData.linkCriteriaPoiTypes}
                handleButtonClick={handlePoiTypesChange}
              />
            </Col>
          </Row>

          <Row className="margin-top-20">
            <Col>
              <CityMultiSelect
                citys={citys}
                selectedCitys={formData.linkCriteriaCitys}
                handleButtonClick={handleCitysChange}
              />
            </Col>
          </Row>

          <Row className="margin-top-20">
            <Col>
              <Button className="btn btn-primary btn-margin-10" type="submit">
                {t('CONFIRM')}
              </Button>
              <Link
                className="btn btn-secondary margin-left-10"
                to={MANAGE_EXTERNAL_LINKS_PATH}
              >
                {t('CANCEL')}
              </Link>
            </Col>
          </Row>
        </Form>
      </Container>
      <Footer />
    </div>
  )
}

export default ManageExternalLinkEdit
